<template>
  <b-card
    v-if="data"
    no-body
    class="card-statistics dashboard-card dashboard-card--stats"
  >
    <b-card-header>
      <div class="d-flex align-items-center">
        <div class="section-dot mr-1"></div>
        <b-card-title class="mb-0">Statistics</b-card-title>
      </div>
      <b-card-text class="font-small-2 mr-25 mb-0">
<!--        Updated 1 month ago-->
      </b-card-text>
    </b-card-header>
    <b-card-body class="statistics-body">
      <b-row>
        <b-col
          v-for="item in data"
          :key="item.icon"
          xl="3"
          sm="6"
          :class="['stat-col', item.customClass]"
        >
          <div class="stat-card d-flex align-items-center">
            <div class="stat-avatar mr-1">
              <b-avatar
                size="46"
                :variant="item.color"
              >
                <feather-icon
                  size="22"
                  :icon="item.icon"
                />
              </b-avatar>
            </div>
            <div class="stat-content">
              <div class="stat-value">
                {{ displayTitle(item) }}
              </div>
              <div class="stat-label">
                {{ item.subtitle }}
              </div>
            </div>
          </div>
        </b-col>
      </b-row>
    </b-card-body>
  </b-card>
</template>

<script>
import {
  BCard, BCardHeader, BCardTitle, BCardText, BCardBody, BRow, BCol, BAvatar,
} from 'bootstrap-vue'

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BCardHeader,
    BCardTitle,
    BCardText,
    BCardBody,
    BAvatar,
  },
  props: {
    data: {
      default: () => [],
    },
  },
  data() {
    return {
      animatedValues: {},
    }
  },
  watch: {
    data: {
      handler() {
        this.animateNumbers()
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    keyFor(item) {
      return item.subtitle || item.icon || item.title
    },
    parseNumber(value) {
      if (value === null || value === undefined) return null
      const raw = String(value).replace(/,/g, '').trim()
      if (!/^\d+(\.\d+)?$/.test(raw)) return null
      const num = Number(raw)
      return Number.isFinite(num) ? num : null
    },
    formatNumber(value) {
      return Number(value).toLocaleString()
    },
    displayTitle(item) {
      const key = this.keyFor(item)
      const animated = this.animatedValues[key]
      if (animated === undefined) {
        return item.title
      }
      if (typeof animated === 'number') {
        return this.formatNumber(animated)
      }
      return animated
    },
    animateNumbers() {
      if (!this.data || !this.data.length) return
      this.data.forEach(item => {
        const key = this.keyFor(item)
        const target = this.parseNumber(item.title)
        if (target === null) {
          this.$set(this.animatedValues, key, item.title)
          return
        }
        const from = Number(this.animatedValues[key] || 0)
        if (from === target) {
          this.$set(this.animatedValues, key, target)
          return
        }
        const duration = 900
        const start = window.performance ? performance.now() : Date.now()
        const step = now => {
          const current = window.performance ? now : Date.now()
          const progress = Math.min((current - start) / duration, 1)
          const value = Math.round(from + (target - from) * progress)
          this.$set(this.animatedValues, key, value)
          if (progress < 1) {
            requestAnimationFrame(step)
          }
        }
        requestAnimationFrame(step)
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.dashboard-card {
  border: 1px solid #eef1f6;
  box-shadow: 0 12px 24px rgba(16, 24, 40, 0.06);
  border-radius: 14px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 18px 30px rgba(16, 24, 40, 0.12);
  }

  .card-header {
    border-bottom: 1px solid #f0f2f6;
    padding: 1.25rem 1.5rem 0.9rem;
    background: linear-gradient(90deg, #f8fafc, #ffffff);
  }
}

.section-dot {
  width: 10px;
  height: 10px;
  border-radius: 999px;
  background: linear-gradient(135deg, #7367f0, #8f85f7);
  box-shadow: 0 0 0 6px rgba(115, 103, 240, 0.08);
  animation: dot-pulse 2.6s ease-in-out infinite;
}

.statistics-body {
  padding: 1rem 1.25rem 1.5rem;
}

.stat-col {
  padding-top: 0.75rem;
  padding-bottom: 0.75rem;
}

.stat-card {
  background: #f9fafc;
  border: 1px solid #eef1f6;
  border-radius: 12px;
  padding: 0.85rem 1rem;
  min-height: 80px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 20px rgba(15, 23, 42, 0.12);
}

.stat-avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 54px;
  height: 54px;
  border-radius: 16px;
  background: #ffffff;
  border: 1px solid #eef1f6;
  box-shadow: 0 6px 14px rgba(15, 23, 42, 0.08);
}

.stat-value {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1f2937;
  line-height: 1.2;
}

.stat-label {
  font-size: 0.85rem;
  color: #6b7280;
}

@keyframes dot-pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(115, 103, 240, 0.2);
  }
  70% {
    box-shadow: 0 0 0 8px rgba(115, 103, 240, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(115, 103, 240, 0);
  }
}

@media (min-width: 992px) {
  .stat-col + .stat-col {
    border-left: 1px dashed rgba(148, 163, 184, 0.35);
  }
}
</style>
